/**
 * Responsibility: offer the searches a row's numbers came from, so no figure
 * has to be taken on trust.
 *
 * This is the answer to the most likely way for this page to be wrong. Every
 * price is a median of whatever eBay returned for a query, and the failure
 * that costs money is a query that matched the wrong thing - a different
 * printing, a Japanese copy, a lot of four. Reading a payoff cannot catch
 * that; looking at the actual results can, in about five seconds.
 *
 * The URLs come from the hub rather than being assembled here, because they
 * have to search exactly what the pricer searched. Rebuilding them in the
 * browser would be a second implementation free to drift from the first, and
 * a link that searched something slightly different would look like
 * confirmation while comparing two different things.
 *
 * Sold links come first in every grouping. That ordering is the same argument
 * the rest of the page makes: what someone paid is a fact, what a seller is
 * asking is an aspiration.
 */
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { Opportunity, RejectedCandidate } from '../../api/client';

type VerifyLinksProps = {
  card: Opportunity | RejectedCandidate;
  /** `row` is a compact single line for a table cell; `panel` is labelled. */
  variant?: 'row' | 'panel';
};

/** One external link, with the attributes an external link needs. */
function Out({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      variant="body2"
      // 44px on touch in both directions, per the mobile contract the kit
      // enforces. Width matters as much as height and was missing: the
      // shortest label here is "raw", which with its icon came to 42px.
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        minHeight: { xs: 44, md: 'auto' },
        minWidth: { xs: 44, md: 'auto' },
      }}
    >
      {children}
      <OpenInNewIcon sx={{ fontSize: 14 }} />
    </Link>
  );
}

export function VerifyLinks({ card, variant = 'panel' }: VerifyLinksProps) {
  const links = card.links;

  if (variant === 'row') {
    return (
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }} useFlexGap>
        <span>
          <Out href={links.raw_sold}>raw</Out>
        </span>

        <span>
          <Out href={links.graded_sold}>PSA 10</Out>
        </span>

        {links.psa_spec ? (
          <span>
            <Out href={links.psa_spec}>pop</Out>
          </span>
        ) : null}
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5} sx={{ minWidth: 0 }}>
      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          Ungraded copies
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Out href={links.raw_sold}>completed sales</Out>
          <Out href={links.raw_listings}>current listings</Out>
        </Stack>
      </Stack>

      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          PSA 10 copies
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Out href={links.graded_sold}>completed sales</Out>
          <Out href={links.graded_listings}>current listings</Out>
        </Stack>
      </Stack>

      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          PSA
        </Typography>
        {links.psa_spec ? (
          <Out href={links.psa_spec}>population report for spec {card.psa_spec_id}</Out>
        ) : (
          <Stack spacing={0.5}>
            <Out href={links.psa_population}>population report</Out>
            <Typography variant="caption" color="text.secondary">
              No spec ID on this card, so this is PSA&apos;s search rather than a direct link.
              Adding the ID makes the gem rate fetch automatically.
            </Typography>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
